import type { EnhancedConversionData } from './enhanced-conversions'

type GtagWindow = Window & {
  gtag?: (...args: unknown[]) => void
  dataLayer?: unknown[]
}

let warnedCall = false
let warnedForm = false

/** Live Ads customer 814-764-7886 snippets from conversion_action.tag_snippets. */
export const DEFAULT_FORM_LABEL = 'AW-11442114292/4WKDCO_u4J8ZEPSdg9Aq'
export const DEFAULT_CALL_LABEL = 'AW-11442114292/u3FcCM-MrJwaEPSdg9Aq'

function resolveLabel(envValue: string | undefined, fallback: string): string {
  return envValue && envValue.length > 0 ? envValue : fallback
}

/**
 * Fires the Google Ads "Click to call" conversion event.
 * Safe to call even if gtag hasn't loaded yet — silently no-ops.
 *
 * @returns true if the event was sent, false if skipped (missing label, SSR, or gtag not loaded).
 */
export function fireCallConversion(): boolean {
  if (typeof window === 'undefined') return false
  const gtag = (window as GtagWindow).gtag
  if (typeof gtag !== 'function') return false

  const label = resolveLabel(process.env.NEXT_PUBLIC_GTAG_CALL_LABEL, DEFAULT_CALL_LABEL)
  if (!label) {
    if (!warnedCall) {
      console.warn(
        '[gtag-conversions] NEXT_PUBLIC_GTAG_CALL_LABEL is not set — call conversion NOT sent',
      )
      warnedCall = true
    }
    return false
  }

  gtag('event', 'conversion', {
    send_to: label,
    value: 1.0,
    currency: 'USD',
  })
  return true
}

/**
 * Fires the GA4 lead event for a form submission.
 *
 * Why this exists separately from `fireFormConversion`: that one sends
 * `send_to: AW-…/<label>`, which routes the event to Google Ads ONLY. GA4 never
 * saw it, so the property recorded `form_start` but zero submissions and could
 * not attribute a single lead.
 *
 * Sent deliberately WITHOUT `send_to` so gtag broadcasts to every destination
 * configured on the page tag (GT-W6J5JWR → GA4 property G-B8N6CMDR7F). Google
 * Ads also receives it but ignores it: `generate_lead` is not a configured
 * conversion action there, and the Ads conversion is a distinct `conversion`
 * event keyed on its own label. The two cannot double-count each other.
 *
 * `generate_lead` is GA4's recommended event for lead generation, so it can be
 * marked as a key event in the GA4 UI without a custom definition. It is also
 * mirrored onto `dataLayer` as `form_submit` so a GTM container can trigger off
 * it later without a code change (none is configured today —
 * `google_tag_manager_id` in the Settings global is empty).
 *
 * @param source Originating form (e.g. 'contact-form'), sent as `form_source`.
 * @returns true if the GA4 event was sent, false if skipped (SSR or gtag not loaded).
 */
export function fireLeadEvent(source?: string): boolean {
  if (typeof window === 'undefined') return false

  const w = window as GtagWindow
  const params: Record<string, unknown> = { value: 1.0, currency: 'USD' }
  if (source) params.form_source = source

  // Pushed independently of gtag readiness so a GTM trigger still sees the lead
  // even if gtag.js was blocked. A frozen/blocked dataLayer must never throw on
  // the thank-you page.
  try {
    w.dataLayer = w.dataLayer || []
    w.dataLayer.push({ event: 'form_submit', ...params })
  } catch {
    // Ignore — tracking must never break the page.
  }

  const gtag = w.gtag
  if (typeof gtag !== 'function') return false

  gtag('event', 'generate_lead', params)
  return true
}

/** How long to wait for the Ads hit before navigating anyway. */
const NAV_TIMEOUT_MS = 2000

/**
 * Fires the full lead bundle (Google Ads + GA4 + Meta) at the moment of a
 * successful submission, then calls `onDone` once the hits are away.
 *
 * This is gtag's documented `event_callback` / `event_timeout` pattern — the
 * one Google ships for "record a conversion, then send the user somewhere".
 * It replaces the previous design, where conversions only fired after
 * `/thank-you` mounted, and so were lost whenever the visitor never got there:
 *
 *  - a FormBlock with a CMS-configured redirect to any other URL counted
 *    nothing at all, on either platform;
 *  - closing the tab during the navigation dropped the lead.
 *
 * The Ads conversion is an image request that does not survive page teardown,
 * hence `event_callback`. GA4 and Meta are sent first because both use
 * `navigator.sendBeacon` internally and do survive unload.
 *
 * Returns whether the bundle was dispatched. When it returns false NOTHING was
 * sent — the caller must leave the pending-lead flag in place so the
 * `/thank-you` fallback remains the single sender. Sending a partial bundle
 * here would double-push `form_submit` onto the dataLayer.
 *
 * @param source Originating form, forwarded to GA4 as `form_source`.
 * @param onDone Invoked exactly once, after the hits are away or on timeout.
 */
export function fireLeadConversions(
  source: string | undefined,
  onDone: () => void,
  enhancedData?: EnhancedConversionData,
): boolean {
  let settled = false
  const finish = () => {
    if (settled) return
    settled = true
    onDone()
  }

  if (typeof window === 'undefined') {
    finish()
    return false
  }

  const w = window as GtagWindow & { fbq?: (...args: unknown[]) => void }

  // gtag loads `afterInteractive`, so a fast submit (or an ad blocker) can beat
  // it. Send nothing and let /thank-you's retry loop own the conversion.
  if (typeof w.gtag !== 'function') {
    finish()
    return false
  }

  fireLeadEvent(source)
  try {
    w.fbq?.('track', 'Lead')
  } catch {
    // A broken pixel must never block the visitor's navigation.
  }

  const label = resolveLabel(process.env.NEXT_PUBLIC_GTAG_FORM_LABEL, DEFAULT_FORM_LABEL)
  if (!label) {
    // GA4 + Meta are away; Ads cannot fire without a label (fireFormConversion
    // logs that). Still counts as dispatched — /thank-you must not re-send.
    finish()
    return true
  }

  if (enhancedData) w.gtag('set', 'user_data', enhancedData)
  w.gtag('event', 'conversion', {
    send_to: label,
    value: 1.0,
    currency: 'USD',
    event_callback: finish,
    event_timeout: NAV_TIMEOUT_MS,
  })

  // gtag processes queued commands in order. Limit contact hashes to this Ads hit.
  if (enhancedData) w.gtag('set', 'user_data', {})

  // Backstop: if gtag never invokes the callback at all, don't strand the
  // visitor on the submitted form.
  window.setTimeout(finish, NAV_TIMEOUT_MS)
  return true
}

/**
 * Fires the Google Ads form submission conversion event.
 * Safe to call even if gtag hasn't loaded yet — silently no-ops.
 *
 * @returns true if the event was sent, false if skipped (missing label, SSR, or gtag not loaded).
 */
export function fireFormConversion(enhancedData?: EnhancedConversionData): boolean {
  if (typeof window === 'undefined') return false
  const gtag = (window as GtagWindow).gtag
  if (typeof gtag !== 'function') return false

  const label = resolveLabel(process.env.NEXT_PUBLIC_GTAG_FORM_LABEL, DEFAULT_FORM_LABEL)
  if (!label) {
    if (!warnedForm) {
      console.warn(
        '[gtag-conversions] NEXT_PUBLIC_GTAG_FORM_LABEL is not set — form conversion NOT sent',
      )
      warnedForm = true
    }
    return false
  }

  if (enhancedData) gtag('set', 'user_data', enhancedData)
  gtag('event', 'conversion', {
    send_to: label,
    value: 1.0,
    currency: 'USD',
  })
  if (enhancedData) gtag('set', 'user_data', {})
  return true
}
